import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../../util/API";
import { Badge, Button, Card, Field, Input } from "./mailUI";

const templateBadgeTone = (source) => {
  if (source === "custom") return "info";
  if (source === "override") return "warning";
  return "neutral";
};

const EmailTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupLoading, setGroupLoading] = useState(false);
  const [updatingKey, setUpdatingKey] = useState("");

  const loadTemplates = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/mail/templates");
      setTemplates(res.data?.data?.items || []);
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load templates"
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const res = await API.get("/mail/template-groups");
      setGroups(res.data?.data?.items || []);
    } catch {
      setGroups([]);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadGroups();
  }, []);

  const allGroupNames = useMemo(() => {
    const names = new Set();
    groups.forEach((g) => names.add(String(g.name || "").trim()));
    templates.forEach((t) => names.add(String(t.group || "General").trim()));
    names.add("General");
    return Array.from(names).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [groups, templates]);

  const groupedTemplates = useMemo(() => {
    const map = new Map();
    templates.forEach((tpl) => {
      const group = String(tpl.group || "General").trim() || "General";
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(tpl);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [templates]);

  const handleCreateGroup = async () => {
    if (!groupName.trim()) return;
    setGroupLoading(true);
    try {
      await API.post("/mail/template-groups", { name: groupName.trim() });
      setGroupName("");
      await loadGroups();
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to create group"
      );
    } finally {
      setGroupLoading(false);
    }
  };

  const handleGroupChange = async (tpl, nextGroup) => {
    if (!tpl || !nextGroup) return;
    setUpdatingKey(tpl.key);
    try {
      await API.put(`/mail/templates/${tpl.key}`, {
        name: tpl.name,
        group: nextGroup,
        subject: tpl.subject,
        text: tpl.text,
        html: tpl.html,
        description: tpl.description,
        variables: Array.isArray(tpl.variables) ? tpl.variables : [],
      });
      await loadTemplates();
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update template group"
      );
    } finally {
      setUpdatingKey("");
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <Card
          title="Template Groups"
          subtitle="Create groups to organize templates and assign them from the table."
        >
          <div className="space-y-4">
            <Field label="New Group Name">
              <Input
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                placeholder="Orders"
              />
            </Field>
            <Button onClick={handleCreateGroup} disabled={groupLoading || !groupName.trim()}>
              {groupLoading ? "Creating..." : "Create Group"}
            </Button>
            {groups.length ? (
              <div className="flex flex-wrap gap-2">
                {groups.map((group) => (
                  <Badge key={group._id || group.name} tone="info">
                    {group.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No custom groups yet.</p>
            )}
          </div>
        </Card>

        <Card
          title="Template Actions"
          subtitle="Open a template to edit content and preview the HTML output."
        >
          <div className="flex flex-wrap gap-3">
            <Button variant="secondary" onClick={() => navigate("/administrator/mail-management/templates/new")}>
              New Template
            </Button>
            <Button variant="secondary" onClick={loadTemplates}>
              Refresh Templates
            </Button>
          </div>
        </Card>
      </div>

      {loading ? (
        <Card title="Template Library">
          <p className="text-sm text-slate-500">Loading templates...</p>
        </Card>
      ) : error ? (
        <Card title="Template Library">
          <p className="text-sm text-rose-600">{error}</p>
        </Card>
      ) : templates.length === 0 ? (
        <Card title="Template Library">
          <p className="text-sm text-slate-500">No templates available.</p>
        </Card>
      ) : (
        groupedTemplates.map(([groupName, items]) => (
          <Card
            key={groupName}
            title={groupName}
            subtitle={`${items.length} template${items.length === 1 ? "" : "s"}`}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Template</th>
                    <th className="px-3 py-2">Key</th>
                    <th className="px-3 py-2">Group</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((tpl) => (
                    <tr key={tpl.key} className="border-t border-slate-200">
                      <td className="px-3 py-2 text-slate-800 font-semibold">{tpl.name}</td>
                      <td className="px-3 py-2 text-slate-500">{tpl.key}</td>
                      <td className="px-3 py-2">
                        <select
                          value={tpl.group || "General"}
                          onChange={(event) => handleGroupChange(tpl, event.target.value)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                          disabled={updatingKey === tpl.key}
                        >
                          {allGroupNames.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={templateBadgeTone(tpl.source)}>
                          {tpl.source === "override" ? "Edited Default" : tpl.source}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Button
                          variant="secondary"
                          onClick={() => navigate(`/administrator/mail-management/templates/${tpl.key}`)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

export default EmailTemplates;
